"use client"

import { memo } from "react"
import ReactMarkdown from "react-markdown"
import rehypeRaw from "rehype-raw"
import remarkGfm from "remark-gfm"

import { useMathJaxRemark } from "@/hooks/useMathJaxRemark"
import { sanitizeLatexContent } from "@/utils/latexFormatting"

interface MarkdownRendererProps {
  className?: string
  constrainAnswerImages?: boolean // Apply size constraints for answer option images
  content: string
  optimizeImages?: boolean // Enable Next.js Image optimization for main question content
}

function MarkdownRenderer({ className = "", constrainAnswerImages = false, content, optimizeImages: _optimizeImages = false }: MarkdownRendererProps) {
  // Sanitize content BEFORE passing to MathJax
  const sanitizedContent = sanitizeLatexContent(content)
  const { preprocessContent, ref, remarkMathjax } = useMathJaxRemark(sanitizedContent)

  // Disable image optimization to prevent hydration mismatches
  // The mutable counter approach caused server/client rendering differences

  return (
    <div
      className={`prose prose-lg max-w-none prose-slate dark:prose-invert text-slate-900 dark:text-white break-words overflow-wrap-anywhere ${className}`}
      ref={ref}
    >
      <ReactMarkdown
        components={{
          a: ({ children, href }) => (
            <a
              className="text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300 underline"
              href={href}
              rel="noopener noreferrer"
              target="_blank"
            >
              {children}
            </a>
          ),
          blockquote: ({ children }) => (
            <blockquote className="border-l-4 border-emerald-500 pl-4 italic text-slate-600 dark:text-slate-400 mb-4">
              {children}
            </blockquote>
          ),
          code: ({ children, className, ...props }) => {
            return (
              <code
                className={`bg-slate-100 dark:bg-slate-700 text-sm ${
                  className || ""
                }`}
                {...props}
              >
                {children}
              </code>
            )
          },
          // Custom styling for other elements
          h1: ({ children }) => (
            <h1 className="text-[1.5em] font-bold text-slate-900 dark:text-white mb-4">
              {children}
            </h1>
          ),
          h2: ({ children }) => (
            <h2 className="text-[1.25em] font-semibold text-slate-900 dark:text-white mb-3">
              {children}
            </h2>
          ),
          h3: ({ children }) => (
            <h3 className="text-[1.125em] font-medium text-slate-900 dark:text-white mb-2 mt-6">
              {children}
            </h3>
          ),
          img: ({ alt, src, ...props }) => {
            // Check if this is a math formula that should stay inline and small
            const isMathFormula = src && typeof src === "string" &&
              (/\/formula_image\//.test(src) || /gif\.latex/.test(src));
            
            // Always use regular img tag to prevent hydration mismatches
            return (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                alt={alt || ""}
                className={
                  constrainAnswerImages
                    ? "max-w-full h-auto max-h-80 max-w-lg"
                    : isMathFormula
                    ? "max-w-full h-auto" // Math formulas: no size constraints, stay inline
                    : "max-w-full h-auto max-h-[800px] rounded-lg shadow-sm object-contain" // Large diagrams: constrained
                }
                src={src}
                style={isMathFormula ? {} : { maxHeight: "800px" }}
                {...props}
              />
            );
          },
          li: ({ children }) => (
            <li className="text-slate-700 dark:text-slate-300 list-outside">
              {children}
            </li>
          ),
          ol: ({ children }) => (
            <ol className="list-decimal list-inside text-slate-700 dark:text-slate-300 mb-4 space-y-2 ml-4">
              {children}
            </ol>
          ),
          p: ({ children }) => (
            <p className="mb-4 leading-relaxed text-slate-900 dark:text-white break-words overflow-wrap-anywhere">
              {children}
            </p>
          ),
          pre: ({ children }) => (
            <pre className="bg-slate-100 dark:bg-slate-700 dark:text-white p-4 rounded-lg my-4">
              {children}
            </pre>
          ),
          table: ({ children }) => (
            <div className="overflow-x-auto mb-4">
              <table className="min-w-full border border-slate-200 dark:border-slate-700 rounded-lg">
                {children}
              </table>
            </div>
          ),
          td: ({ children }) => (
            <td className="px-4 py-2 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300">
              {children}
            </td>
          ),
          th: ({ children }) => (
            <th className="px-4 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-left font-medium text-slate-900 dark:text-white">
              {children}
            </th>
          ),
          ul: ({ children }) => (
            <ul className="list-disc list-inside text-slate-700 dark:text-slate-300 mb-4 space-y-2 ml-4">
              {children}
            </ul>
          )
        }}
        rehypePlugins={[rehypeRaw]}
        remarkPlugins={[remarkGfm, remarkMathjax]}
      >
        {preprocessContent(sanitizedContent)}
      </ReactMarkdown>
    </div>
  )
}

export default memo(MarkdownRenderer)
